/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { IconButton, LinkButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import { IntlMessagesProvider } from '@atlaskit/intl-messages-provider';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import defaultMessages from '../../i18n/en';
import { fetchMessagesForLocale } from '../../i18n/fetch-messages-for-locale';

import appTiles from './assets/twc-app-tiles.svg';
import { messages } from './messages';

const marginStyles = cssMap({
	none: {},
	'space.150': {
		marginTop: token('space.150'),
		marginRight: token('space.150'),
		marginBottom: token('space.150'),
		marginLeft: token('space.150'),
	},
});

const styles = cssMap({
	root: {
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.small'),
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	appTilesWrapper: {
		marginTop: token('space.150'),
	},
});

export type TwcSampleBannerProps = {
	href: string;
	locale?: string;
	margin?: 'none' | 'space.150';
	messageInstanceId?: string;
	onClose?: () => void;
	onTryNowClick?: () => void;
	product: 'jira' | 'confluence';
	testId?: string;
};

const TwcSampleBannerContent = ({
	href,
	locale,
	margin = 'none',
	messageInstanceId,
	onClose,
	onTryNowClick,
	product,
	testId = 'platform-growth-twc-sample-banner',
}: TwcSampleBannerProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcSampleBanner',
			attributes: { product, messageInstanceId, locale },
		}).fire('growth');
	}, [createAnalyticsEvent, product, messageInstanceId, locale]);

	const handleClose = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcSampleBannerClose',
			attributes: { product, messageInstanceId, locale },
		}).fire('growth');
		onClose?.();
	};

	const handleTryNowClick = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcSampleBannerTryNow',
			attributes: { product, messageInstanceId, locale },
		}).fire('growth');
		onTryNowClick?.();
	};

	const testIdWithMessageInstanceId = [testId, messageInstanceId].filter(Boolean).join('_');

	return (
		<Box xcss={cx(styles.root, marginStyles[margin])} testId={testIdWithMessageInstanceId}>
			<Stack spread="space-between" space="space.150">
				<Inline spread="space-between">
					<Box xcss={styles.appTilesWrapper}>
						<Image
							src={appTiles}
							srcDark={appTiles}
							alt={formatMessage(messages.appTilesAlt)}
							testId="twc-sample-banner-app-tiles"
							width={91}
						/>
					</Box>
					<IconButton
						icon={(iconProps) => <CrossIcon {...iconProps} size="small" />}
						onClick={handleClose}
						label={formatMessage(messages.closeLabel)}
						testId="twc-sample-banner-close"
						appearance="subtle"
					/>
				</Inline>
				<Stack spread="space-between" space="space.100">
					<Heading size="small" testId="twc-sample-banner-heading">
						<FormattedMessage {...messages.heading} />
					</Heading>
					<Text size="small" color="color.text.subtle" testId="twc-sample-banner-subheading">
						{product === 'jira' ? (
							<FormattedMessage {...messages.subheadingJira} />
						) : (
							<FormattedMessage {...messages.subheadingConfluence} />
						)}
					</Text>
				</Stack>
				<Inline alignBlock="start">
					<LinkButton
						appearance="default"
						href={href}
						testId="twc-sample-banner-button-try-now"
						onClick={handleTryNowClick}
						target="_blank"
					>
						<FormattedMessage {...messages.tryNowButton} />
					</LinkButton>
				</Inline>
			</Stack>
		</Box>
	);
};

export const TwcSampleBanner = (props: TwcSampleBannerProps): JSX.Element => (
	<IntlMessagesProvider defaultMessages={defaultMessages} loaderFn={fetchMessagesForLocale}>
		<TwcSampleBannerContent {...props} />
	</IntlMessagesProvider>
);
